import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createSelector } from "@reduxjs/toolkit";
import { Mail, MessageSquare, Phone, Search, UserPlus } from "lucide-react";

import { cn } from "lib/utils";
import { getAvatarUrl } from "helpers/avatar";
import { getUserList } from "slices/users/thunk";
import Loading from "Common/Components/Loading";

const fullName = (u: any) =>
  [u?.name, u?.surname].filter(Boolean).join(" ").trim() ||
  u?.userName ||
  "Unknown";

const Friends = () => {
  document.title = "Friends | EngageHub IQ";

  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const selectUsers = createSelector(
    (state: any) => state.Users,
    (u) => ({ userList: u?.userList ?? [], loading: u?.loading })
  );
  const { userList, loading } = useSelector(selectUsers);

  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showDetailMobile, setShowDetailMobile] = useState(false);

  useEffect(() => {
    dispatch(getUserList());
  }, [dispatch]);

  const friends = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return userList;
    return userList.filter(
      (u: any) =>
        fullName(u).toLowerCase().includes(q) ||
        u?.userName?.toLowerCase().includes(q) ||
        u?.email?.toLowerCase().includes(q)
    );
  }, [userList, search]);

  const selected = useMemo(
    () => userList.find((u: any) => u.id === selectedId),
    [userList, selectedId]
  );

  const handleSelect = (u: any) => {
    setSelectedId(u.id);
    setShowDetailMobile(true);
  };

  // Jump straight into a private conversation with this user.
  const handleMessage = (u: any) => {
    navigate("/communication", { state: { userId: u.id } });
  };

  return (
    <div className="-m-4 sm:-m-6 lg:-m-8">
      <div
        className={cn(
          "flex h-[calc(100vh-4rem)] w-full overflow-hidden",
          "border-y border-border bg-card/30 backdrop-blur-sm xl:rounded-2xl xl:border"
        )}
      >
        {/* Friend list */}
        <div
          className={cn(
            "flex flex-col xl:w-80 xl:shrink-0 xl:border-r xl:border-border/60",
            showDetailMobile ? "hidden xl:flex" : "flex w-full"
          )}
        >
          <div className="border-b border-border/60 px-4 py-4">
            <h2 className="text-base font-semibold text-foreground">Friends</h2>
            <p className="text-xs text-muted-foreground">
              People you can chat with
            </p>
            <div className="mt-3 flex items-center gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search friends…"
                className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-6">
                <Loading size="sm" color="blue" isVisible={true} />
              </div>
            ) : friends.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">
                No friends found.
              </div>
            ) : (
              friends.map((u: any) => {
                const isActive = u.id === selectedId;
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => handleSelect(u)}
                    className={cn(
                      "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors",
                      isActive ? "bg-primary/10" : "hover:bg-muted/60"
                    )}
                  >
                    <span className="relative shrink-0">
                      <img
                        src={getAvatarUrl(u.email || u.userName || u.id)}
                        alt={fullName(u)}
                        className="h-10 w-10 rounded-full bg-muted"
                      />
                      <span
                        className={cn(
                          "absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-card",
                          u.isActive ? "bg-success" : "bg-muted-foreground"
                        )}
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-foreground">
                        {fullName(u)}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {u.email || u.userName}
                      </span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Detail panel */}
        <div
          className={cn(
            "min-w-0 flex-1 flex-col",
            showDetailMobile ? "flex w-full" : "hidden xl:flex"
          )}
        >
          {selected ? (
            <div className="flex h-full flex-col">
              <div className="flex items-center gap-2 border-b border-border/60 px-4 py-3 xl:hidden">
                <button
                  type="button"
                  onClick={() => setShowDetailMobile(false)}
                  className="text-sm font-medium text-primary"
                >
                  ← Back
                </button>
              </div>

              <div className="flex flex-1 flex-col items-center px-6 py-10 text-center">
                <span className="relative">
                  <img
                    src={getAvatarUrl(
                      selected.email || selected.userName || selected.id
                    )}
                    alt={fullName(selected)}
                    className="h-28 w-28 rounded-full bg-muted shadow-soft-lg"
                  />
                  <span
                    className={cn(
                      "absolute bottom-1 right-1 h-5 w-5 rounded-full border-4 border-card",
                      selected.isActive ? "bg-success" : "bg-muted-foreground"
                    )}
                  />
                </span>

                <h3 className="mt-4 text-xl font-semibold text-foreground">
                  {fullName(selected)}
                </h3>
                <p className="text-sm text-muted-foreground">
                  @{selected.userName}
                </p>
                <span
                  className={cn(
                    "mt-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium",
                    selected.isActive
                      ? "bg-success/10 text-success"
                      : "bg-muted text-muted-foreground"
                  )}
                >
                  <span
                    className={cn(
                      "h-1.5 w-1.5 rounded-full",
                      selected.isActive ? "bg-success" : "bg-muted-foreground"
                    )}
                  />
                  {selected.isActive ? "Online" : "Offline"}
                </span>

                <div className="mt-6 w-full max-w-sm space-y-3 text-left">
                  <div className="flex items-center gap-3 rounded-lg border border-border/60 bg-background px-4 py-3">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="truncate text-sm text-foreground">
                      {selected.email || "—"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 rounded-lg border border-border/60 bg-background px-4 py-3">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="truncate text-sm text-foreground">
                      {selected.phoneNumber || "—"}
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleMessage(selected)}
                  className={cn(
                    "mt-8 inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5",
                    "text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                  )}
                >
                  <MessageSquare className="h-4 w-4" />
                  Message
                </button>
              </div>
            </div>
          ) : (
            <div className="hidden flex-1 items-center justify-center text-center xl:flex">
              <div className="max-w-sm space-y-3 px-6">
                <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-primary/10 text-primary">
                  <UserPlus className="h-6 w-6" />
                </div>
                <h3 className="text-base font-semibold text-foreground">
                  Select a friend
                </h3>
                <p className="text-sm text-muted-foreground">
                  Pick someone from the list to see their profile and start a
                  conversation.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Friends;
